/** @format */

import {
	AnalyticsService,
	BreakpointsService,
	FileService,
	UtilityService
} from '@shared/data-access/services';

import {
	Component,
	ComponentRef,
	DestroyRef,
	effect,
	inject,
	input,
	OnInit,
	TemplateRef,
	viewChild
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { DynamicDrawerSizes, ProductSelectionUtilityService } from '@pdp/utils';
import { HmfDrawerComponent, HmfModalFactoryService } from '@dsg-tech/homefield-angular';
import { HomrDataService, HomrPage } from '@shared/homr/data-access';
import { ProductStyle, Shipping } from '@shared/data-access/interfaces';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { selectPrimaryCategory, selectShippingData } from '@pdp/data-access/state';

import { DEFAULT_SHIPPING_MESSAGES } from './default-shipping-messages.constants';

import {
	homefieldIconAlertErrorOutlined,
	HomefieldIconLibraryModule,
	homefieldIconOffer,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { HtmlSanitizerPipe } from '@shared/utils/pipes';
import { Store as NgRxStore } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { pdpJsonFileName } from '@pdp/data-access/constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface IShippingMessage {
	date?: number;
	emsName: string;
	image: string;
	imageClass: string;
	innerHtml: string;
	message: string;
	order: number;
	show: boolean;
	showImage: boolean;
	subMessage: string;
	type: string;
}

export interface IShippingAttributes {
	date?: number;
	innerHtml?: string;
	message?: string;
	show?: boolean;
	subMessage?: string;
}

@Component({
	selector: 'pdp-component-shipping',
	templateUrl: './shipping.component.html',
	styleUrl: './shipping.component.scss',
	imports: [DatePipe, HomefieldIconLibraryModule, HtmlSanitizerPipe]
})
export class ShippingComponent implements OnInit {
	private readonly fileService = inject(FileService);
	private readonly ngRxstore = inject(NgRxStore);
	private readonly productSelectionUtilityService = inject(ProductSelectionUtilityService);
	private readonly analyticsService = inject(AnalyticsService);
	private readonly homrDataService = inject(HomrDataService);
	private readonly modalFactoryService = inject(HmfModalFactoryService);
	private readonly breakpointsService = inject(BreakpointsService);
	private readonly destroyRef = inject(DestroyRef);

	productStyle = input<ProductStyle>();
	/**
	 * @description
	 * Enable pre order text and date change
	 */
	preOrderEnabled = input<boolean>();

	/**
	 * Shipping Message details panel header title
	 */
	heading = 'Shipping Details';

	/**
	 * @description
	 * Flag to determine if the product is in the "bike" category based on the calc code (10 and 20 should not display oversized shipping messaging)
	 */
	isBikeCategory = false;

	/**
	 * @description
	 * Shipping messages to be displayed
	 */
	shippingMessages: IShippingMessage[];

	/**
	 * Flag to determine if shipping messages should be displayed
	 */
	shouldDisplayShippingMessages = false;

	/**
	 * Flag to determine if ship lead message should be displayed
	 */
	shouldHideShipLeadMessage = false;

	/**
	 * @description
	 * Shipping info observable.
	 */
	shippingInfo: Shipping | Record<string, never>;

	/**
	 * @description
	 * Current shipping message that the user has selected.
	 */
	currentShippingMessage: IShippingMessage;

	private drawerRef: ComponentRef<HmfDrawerComponent>;
	drawerContent = viewChild.required<TemplateRef<unknown>>('drawerContent');

	constructor(homefieldIconsLibraryService: HomefieldIconsLibraryService) {
		homefieldIconsLibraryService.registerIcons([
			homefieldIconAlertErrorOutlined,
			homefieldIconOffer
		]);
		effect(() => {
			const breakpoint = this.breakpointsService.breakpoint();
			if (this.drawerRef) {
				this.drawerRef.setInput(
					'size',
					this.productSelectionUtilityService.getValueByScreenSize(
						DynamicDrawerSizes,
						breakpoint
					)
				);
			}
		});
	}

	ngOnInit() {
		this.initializeObservables();
	}

	setupShippingMessageDisplay() {
		this.shippingMessages = this.getShippingMessagesToDisplay(this.shippingInfo);
	}

	/**
	 * @description
	 * Accepts the lead time number and builds the appropriate message
	 *
	 * @param leadTime
	 */
	getLeadTimeMessage(leadTime: number): string {
		let leadMessage;

		if (leadTime > 2 && leadTime < 11) {
			leadMessage = `Usually ships in ${leadTime} business days.`;
		} else if (leadTime % 5 === 0 && leadTime <= 55) {
			leadMessage = `Usually ships in ${leadTime / 5} weeks.`;
		} else if (leadTime % 5 !== 0 && leadTime <= 55) {
			// Calculate number of weeks (Using bitwise operator to round)
			// eslint-disable-next-line no-bitwise
			leadMessage = `Usually ships in ${(leadTime / 5) >> 0} - ${
				// eslint-disable-next-line no-bitwise
				(leadTime / 5 + 1) >> 0
			} weeks.`;
		} else {
			leadMessage = 'Usually ships in 11 or more weeks.';
		}

		return leadMessage;
	}

	private determineDisplayableShipMessages(shippingInfo: Shipping | Record<string, never>) {
		const isLTL = shippingInfo?.shippingClass?.indexOf('LTL') > -1;
		const isOP = shippingInfo?.shippingClass?.indexOf('OP') > -1;
		const isP = shippingInfo?.shippingClass?.indexOf('P') > -1;

		return {
			LTL: isLTL,
			OP: isOP,
			standard: isP || isOP,
			restriction: shippingInfo?.isHazmat || shippingInfo?.isRestricted,
			leadTime:
				shippingInfo?.leadTime > 2 &&
				!shippingInfo?.isHotMarket &&
				!this.shouldHideShipLeadMessage &&
				!shippingInfo?.isPreSale,
			preOrder:
				shippingInfo?.isPreSale &&
				!shippingInfo?.comingSoon &&
				UtilityService.isDateAfterNowDate(shippingInfo.preSaleDate, 'MM-dd-yyyy')
		};
	}

	/**
	 * @description
	 * Uses a Shipping object to determine the shipping messages to be displayed
	 *
	 * @param shippingInfo
	 */
	getShippingMessagesToDisplay(
		shippingInfo: Shipping | Record<string, never>
	): IShippingMessage[] {
		// Reset the display flag
		this.shouldDisplayShippingMessages = false;

		let displayedShippingMessages: IShippingMessage[] = JSON.parse(
			JSON.stringify(DEFAULT_SHIPPING_MESSAGES)
		);

		const displayableMessages = this.determineDisplayableShipMessages(shippingInfo);

		if (displayableMessages.LTL) {
			displayedShippingMessages = this.setShippingAttributesForType(
				displayedShippingMessages,
				'LTL',
				{ show: true }
			);

			// Reset the display flag
			this.shouldDisplayShippingMessages = true;
		}

		if (displayableMessages.OP) {
			displayedShippingMessages = this.setShippingAttributesForType(
				displayedShippingMessages,
				'OP',
				{ show: !this.isBikeCategory }
			);

			this.shouldDisplayShippingMessages ||= !this.isBikeCategory;
		}

		if (displayableMessages.standard) {
			displayedShippingMessages = this.setShippingAttributesForType(
				displayedShippingMessages,
				'Standard',
				{ show: false }
			);
		}

		if (displayableMessages.restriction) {
			displayedShippingMessages = this.setShippingAttributesForType(
				displayedShippingMessages,
				'Restriction',
				{ show: true }
			);

			// Reset the display flag
			this.shouldDisplayShippingMessages = true;
		}

		if (displayableMessages.leadTime) {
			displayedShippingMessages = this.setShippingAttributesForType(
				displayedShippingMessages,
				'LeadTime',
				{ show: true, message: this.getLeadTimeMessage(shippingInfo.leadTime) }
			);

			// Reset the display flag
			this.shouldDisplayShippingMessages = true;
		}

		if (displayableMessages.preOrder) {
			if (this.preOrderEnabled()) {
				displayedShippingMessages = this.setShippingAttributesForType(
					displayedShippingMessages,
					'PreOrder',
					{
						show: true,
						subMessage: 'Ships after ',
						date: this.productSelectionUtilityService.formatDateToTimestamp(
							shippingInfo.preSaleDate
						)
					}
				);
			} else {
				displayedShippingMessages = this.setShippingAttributesForType(
					displayedShippingMessages,
					'PreOrder',
					{
						show: true,
						subMessage: `Available to start shipping ${shippingInfo.preSaleDate}`
					}
				);
			}

			// Reset the display flag
			this.shouldDisplayShippingMessages = true;
		}

		// Sort by display value
		displayedShippingMessages.sort((a: IShippingMessage, b: IShippingMessage) =>
			a.order > b.order ? 1 : -1
		);

		return displayedShippingMessages;
	}

	openDrawer() {
		if (!this.drawerRef) {
			this.drawerRef = this.modalFactoryService.create(
				HmfDrawerComponent,
				{
					headerText: this.heading,
					closable: true,
					mobilePeek: true,
					direction: 'right',
					isCloseButtonSticky: true,
					size: this.productSelectionUtilityService.getValueByScreenSize(
						DynamicDrawerSizes,
						this.breakpointsService.breakpoint()
					)
				},
				this.drawerContent()
			);
		}

		this.drawerRef.instance.openDrawer();
		this.sendModalOpenAnalytics(this.heading);
	}

	/**
	 * @description
	 * Loads the shipping message details JSON
	 *
	 * @param shippingMessage
	 */
	loadBodyContent(shippingMessage: IShippingMessage) {
		if (typeof shippingMessage !== 'undefined' && shippingMessage.innerHtml === '') {
			this.homrDataService
				.getData$('static', 2, shippingMessage.emsName)
				.pipe(take(1))
				.subscribe((homrTemplate: HomrPage) => {
					const content =
						homrTemplate.content['Informationals Content'] ||
						homrTemplate.content['informationalsContent'] ||
						homrTemplate.content['Informational Details Alpha'];
					if (content?.[0]) {
						const htmlContent = decodeURIComponent(content[0].wysiwyg);
						this.setShippingAttributesForType(
							this.shippingMessages,
							shippingMessage.type,
							{
								innerHtml: htmlContent
							}
						);
					}
				});
		}
		this.currentShippingMessage = shippingMessage;
		this.openDrawer();
	}

	/**
	 * @description
	 * Accepts an array of IShippingMessage objects and iterates them looking for the provided type and assigns any value overrides
	 * that are provided
	 *
	 * @param shippingMessages
	 * @param shippingType
	 * @param shippingAttributes
	 */
	setShippingAttributesForType(
		shippingMessages: IShippingMessage[],
		shippingType: string,
		shippingAttributes: IShippingAttributes = { show: false }
	): IShippingMessage[] {
		// Ensure there is something to iterate
		if (Array.isArray(shippingMessages) && shippingMessages.length > 0) {
			// Iterate the shipping messages
			for (let i = 0, length = shippingMessages.length; i < length; i += 1) {
				// Ensure there is a defined object at the index and if the current shipping message matches the argument
				if (
					typeof shippingMessages[i] !== 'undefined' &&
					shippingMessages[i].type === shippingType
				) {
					// Assign any attributes
					shippingMessages[i] = Object.assign(shippingMessages[i], shippingAttributes);
					// Break out to prevent unnecessary looping
					break;
				}
			}
		}
		// Return the shipping messages
		return shippingMessages;
	}

	initializeObservables() {
		const observableArray = [
			this.ngRxstore.select(selectShippingData).pipe(
				takeUntilDestroyed(this.destroyRef),
				filter((shippingData) => shippingData != null)
			),
			this.fileService.getDataFileByType$(pdpJsonFileName.brandsData, 'json'),
			this.initializeBikeCategoryObservable()
		];

		combineLatest(observableArray)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				const [shippingData, brandJsonData, primaryCategoryData] = result;

				this.shippingInfo = shippingData;

				// Check to see if the product is a brand that should hide ship lead messages
				if (shippingData !== undefined && brandJsonData !== undefined) {
					this.shouldHideShipLeadMessage = this.shouldBrandHideShipLeadMessage(
						brandJsonData.hideLeadTimeBrands
					);
				}

				if (!shippingData.isHotMarket && primaryCategoryData) {
					// Check the note field as primary name, and fallback to standard category name
					const categoryName =
						typeof primaryCategoryData.note === 'string' &&
						primaryCategoryData.note.trim() !== ''
							? primaryCategoryData.note
							: primaryCategoryData.name;

					// Get the categories JSON file
					this.fileService
						.getDataFileByType$(pdpJsonFileName.categoriesData, 'json')
						.subscribe({
							next: (jsonData) => {
								this.isBikeCategory =
									jsonData?.oversizeShippingExclusion?.indexOf(categoryName) > 0;

								this.setupShippingMessageDisplay();
							},
							error: () => {
								this.isBikeCategory = false;
								this.setupShippingMessageDisplay();
							}
						});
				} else {
					this.setupShippingMessageDisplay();
				}
			});
	}

	public shouldBrandHideShipLeadMessage(brands: string[]): boolean {
		const brandAttributeName = 'X_BRAND';
		return !!this.productStyle()?.descriptiveAttributes.find((attrs) => {
			if (attrs.identifier === brandAttributeName) {
				return brands.includes(attrs.value.toString());
			}
		});
	}

	/**
	 * @description
	 * Subscribes to the app Store and gets the PRIMARY_CATEGORY value
	 */
	private initializeBikeCategoryObservable() {
		return this.ngRxstore.select(selectPrimaryCategory).pipe(distinctUntilChanged());
	}

	/**
	 * @description Calls analytics service to trigger an event for the modal being opened.
	 */
	private sendModalOpenAnalytics(name: string) {
		/*
		 * Analytics Event
		 */
		// Set the modal name
		this.analyticsService.setModalName(name);

		// Set the Analytics event type
		this.analyticsService.setType('event', 'ModalOpen');

		// Trigger the Analytics update
		this.analyticsService.triggerAnalyticsEvent('reporting:update');
	}

	trackByFn(index: number, item: IShippingMessage): string {
		return index.toString() + item.emsName;
	}
}
